import Webcam from "react-webcam";
import { useGeolocated } from "react-geolocated";
import piexif from "piexifjs";
import { useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";

import {
  Dialog,
  DialogActions,
  DialogContent,
  Icon,
  IconButton,
  Snackbar,
  Alert,
  Box,
} from "@mui/material";

import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import AppConfig from "appConfig";

function GeoTaggedCameraCapture({ onCapture }) {
  const webcamRef = useRef(null);

  const [openCamera, setOpenCamera] = useState(false);
  const [cameraUsed, setCameraUsed] = useState(false);
  const [image, setImage] = useState(null);

  const [errorMsg, setErrorMsg] = useState("");
  const [showError, setShowError] = useState(false);

  const { coords } = useGeolocated({
    positionOptions: { enableHighAccuracy: true },
    userDecisionTimeout: 5000,
  });

  const videoConstraints = {
    facingMode: cameraUsed ? "user" : { exact: "environment" },
  };

  /* -----------------------------
     Reverse geocode location
  ----------------------------- */
  async function getGeocodeData(lat, lng) {
    const apiKey = AppConfig.google_map_key;

    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;

    const response = await fetch(url);
    if (!response.ok) return null;

    const data = await response.json();

    if (data.results.length > 0) {
      return data.results[0].formatted_address;
    }

    return null;
  }

  /* -----------------------------
     Watermark image
  ----------------------------- */
  async function addWatermark(base64, text) {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = base64;
    });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = img.width;
    canvas.height = img.height;

    ctx.drawImage(img, 0, 0);

    /* SMALLER FONT */
    const fontSize = Math.round(img.width * 0.032); // ~2.5% of width
    const lineHeight = fontSize * 1.25;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.font = `${fontSize}px Arial`;
    ctx.textBaseline = "top";

    /* Limit watermark width */
    const maxWidth = img.width * 0.5;

    const paragraphs = text.split(/\r?\n/);
    const wrappedLines = [];

    for (const para of paragraphs) {
      const words = para.split(" ");
      let line = "";

      for (const word of words) {
        const testLine = line ? `${line} ${word}` : word;
        const width = ctx.measureText(testLine).width;

        if (width > maxWidth) {
          wrappedLines.push(line);
          line = word;
        } else {
          line = testLine;
        }
      }

      if (line) wrappedLines.push(line);
    }

    const textWidths = wrappedLines.map((l) => ctx.measureText(l).width);
    const textWidth = Math.max(...textWidths);
    const textHeight = wrappedLines.length * lineHeight;

    const padding = Math.round(fontSize * 0.4);

    const x = img.width - textWidth - padding * 2;
    const y = img.height - textHeight - padding * 2;

    /* background */
    ctx.fillStyle = "rgba(0,0,0,0.45)";
    ctx.fillRect(
      x - padding,
      y - padding,
      textWidth + padding * 2,
      textHeight + padding * 2,
    );

    /* text */
    ctx.fillStyle = "#fff";

    wrappedLines.forEach((line, i) => {
      ctx.fillText(line, x, y + i * lineHeight);
    });

    return canvas.toDataURL("image/jpeg", 0.92);
  }

  /* -----------------------------
     Add EXIF + GPS metadata
  ----------------------------- */
  async function geoTagImage(imageSrc, lat, lng, meta) {
    if (!imageSrc) return;

    const m = 1000000;
    const xlat = lat * m;
    const xlng = lng * m;

    const ts = new Date();
    const tsFormatted = ts.toLocaleString();

    const gpsText = `GPS: ${lat}, ${lng}
    ${tsFormatted}
    Laddar Africa`;

    const watermarkedImg = await addWatermark(imageSrc, gpsText);

    const zeroth = {};
    zeroth[piexif.ImageIFD.Make] = "Laddar.Africa";
    zeroth[piexif.ImageIFD.Model] = "LaddarOS";
    zeroth[piexif.ImageIFD.Software] = "AgentApp_DataForm";

    const gps = {};
    gps[piexif.GPSIFD.GPSLatitudeRef] = lat > 0 ? "N" : "S";
    gps[piexif.GPSIFD.GPSLatitude] = [xlat, m];
    gps[piexif.GPSIFD.GPSLongitudeRef] = lng > 0 ? "E" : "W";
    gps[piexif.GPSIFD.GPSLongitude] = [xlng, m];

    const exif = {};
    exif[piexif.ExifIFD.DateTimeOriginal] = ts;
    exif[piexif.ExifIFD.UserComment] = meta;

    // Combine EXIF data
    const exifObj = { "0th": zeroth, Exif: exif, GPS: gps };
    const exifStr = piexif.dump(exifObj);

    // Insert EXIF into image
    const newImage = piexif.insert(exifStr, watermarkedImg);
    return newImage;
  }

  /* -----------------------------
     Capture Image
  ----------------------------- */
  const capture = useCallback(async () => {
    if (!coords) {
      setErrorMsg("Unable to retrieve your location.");
      setShowError(true);
      return;
    }

    const imageSrc = webcamRef.current.getScreenshot();

    if (!imageSrc) {
      setErrorMsg("Failed to capture image.");
      setShowError(true);
      return;
    }

    const lat = parseFloat(coords.latitude).toFixed(6);
    const lng = parseFloat(coords.longitude).toFixed(6);

    const taggedImage = await geoTagImage(
      imageSrc,
      lat,
      lng,
      "Captured using Laddar Africa Data Forms. https://laddar.africa/",
    );

    const payload = {
      image: taggedImage,
      gps: {
        latitude: lat,
        longitude: lng,
        timestamp: new Date().toISOString(),
      },
    };

    setImage(taggedImage);

    if (onCapture) {
      onCapture(payload);
    }

    setOpenCamera(false);
  }, [coords, onCapture]);

  const clearImage = () => {
    setImage(null);
    if (onCapture) onCapture(null);
  };

  return (
    <Box>
      <Snackbar
        open={showError}
        autoHideDuration={5000}
        onClose={() => setShowError(false)}
      >
        <Alert severity="error">{errorMsg}</Alert>
      </Snackbar>

      {/* Photo Container */}
      <Box
        sx={{
          border: "2px dashed #d2d6da",
          borderRadius: "12px",
          height: 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          backgroundColor: "#f8f9fa",
        }}
      >
        {!image && (
          <Box textAlign="center">
            <IconButton
              onClick={() => setOpenCamera(true)}
              sx={{
                width: 70,
                height: 70,
                backgroundColor: "#e3f2fd",
              }}
            >
              <Icon fontSize="large">photo_camera</Icon>
            </IconButton>

            <MDTypography variant="caption" display="block" mt={1}>
              Tap to capture photo
            </MDTypography>
          </Box>
        )}

        {image && (
          <>
            <img
              src={image}
              alt="captured"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />

            <IconButton
              onClick={clearImage}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                width: 40,
                height: 40,
                backgroundColor: "#fff",
                border: "1px solid rgba(0,0,0,0.2)",
                boxShadow: 2,
              }}
            >
              <Icon color="error">close</Icon>
            </IconButton>
          </>
        )}
      </Box>

      {/* Camera Dialog */}
      <Dialog open={openCamera}>
        <DialogContent>
          <Webcam
            ref={webcamRef}
            width="100%"
            screenshotFormat="image/jpeg"
            screenshotQuality={1}
            videoConstraints={videoConstraints}
            mirrored={cameraUsed}
          />
        </DialogContent>

        <DialogActions>
          <MDButton
            color="primary"
            iconOnly
            circular
            variant="outlined"
            onClick={() => setOpenCamera(false)}
          >
            <Icon>close</Icon>
          </MDButton>

          <MDButton
            color="warning"
            iconOnly
            circular
            variant="outlined"
            onClick={() => setCameraUsed(!cameraUsed)}
          >
            <Icon>cameraswitch</Icon>
          </MDButton>

          <MDButton
            color="info"
            iconOnly
            circular
            variant="outlined"
            onClick={capture}
          >
            <Icon>photo_camera</Icon>
          </MDButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

GeoTaggedCameraCapture.propTypes = {
  onCapture: PropTypes.func,
};

export default GeoTaggedCameraCapture;
